import { isAllergyCorrectionPrompt } from '@stell/shared';
import type { ThreadMessage } from './MessageThread';
import type { ToolCallSummary } from './ToolCallTrace';
import type { MemoryChipEntry } from './useAssistantStream';
import type { AiConversationMessage } from './useConversations';

type ViewerRole = 'admin' | 'teen' | 'child';

interface ParsedToolUse {
  id: string;
  name: string;
  input: unknown;
}

interface ParsedContent {
  text: string;
  hasImage: boolean;
  toolUses: ParsedToolUse[];
}

/**
 * #212: `remember`/`forget`-kallene i samtalen, slått opp på `tool_use_id`.
 * `input` er argumentene modellen sendte — der står `remember`-teksten og
 * `replaces_ref`, som verktøyresultatet (`{ ok, ref, status }`) ikke har.
 */
type MemoryToolUses = Map<string, ParsedToolUse>;

/** En `tool_result`-rad: hvilket kall den svarer på, og verdien verktøyet returnerte. */
interface ParsedToolResult {
  toolUseId: string;
  value: unknown;
}

interface ToolResultPayload {
  tool_use_id?: string;
  content?: unknown;
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function parseToolUse(
  block: unknown,
  fallbackId: string | null,
  fallbackName: string | null,
): ParsedToolUse | null {
  if (!block || typeof block !== 'object') return null;
  const b = block as { type?: unknown; id?: unknown; name?: unknown; input?: unknown };
  if (b.type !== 'tool_use') return null;
  const id = typeof b.id === 'string' && b.id.length > 0 ? b.id : fallbackId;
  const name = typeof b.name === 'string' && b.name.length > 0 ? b.name : fallbackName;
  if (!id || !name) return null;
  return { id, name, input: b.input };
}

function parseContent(
  raw: string,
  fallbackToolId: string | null,
  fallbackToolName: string | null,
): ParsedContent {
  const parsed = parseJson(raw);

  if (typeof parsed === 'string') {
    return { text: parsed, hasImage: false, toolUses: [] };
  }

  if (!Array.isArray(parsed)) {
    const toolUse = parseToolUse(parsed, fallbackToolId, fallbackToolName);
    return {
      text: '',
      hasImage: false,
      toolUses: toolUse ? [toolUse] : [],
    };
  }

  const textParts: string[] = [];
  let hasImage = false;
  const toolUses: ParsedToolUse[] = [];

  for (const block of parsed) {
    if (!block || typeof block !== 'object') continue;
    const b = block as { type?: unknown; text?: unknown };
    if (b.type === 'text' && typeof b.text === 'string') {
      textParts.push(b.text);
      continue;
    }
    if (b.type === 'image') {
      hasImage = true;
      continue;
    }
    const toolUse = parseToolUse(block, fallbackToolId, fallbackToolName);
    if (toolUse) toolUses.push(toolUse);
  }

  return { text: textParts.join('').trim(), hasImage, toolUses };
}

function summariseToolResult(value: unknown): string | undefined {
  if (Array.isArray(value)) return `${value.length} treff`;
  if (!value || typeof value !== 'object') return undefined;
  const v = value as { ok?: unknown; error?: unknown; message?: unknown };
  if (v.ok === true) return 'OK';
  if (typeof v.error === 'string') return 'Feilet';
  if (typeof v.message === 'string' && v.message.trim().length > 0) return v.message.trim();
  return undefined;
}

function parseToolResult(row: AiConversationMessage): ParsedToolResult | null {
  const parsed = parseJson(row.content_json);
  if (!parsed || typeof parsed !== 'object') return null;
  const payload = parsed as ToolResultPayload;
  if (typeof payload.tool_use_id !== 'string') return null;
  const value =
    typeof payload.content === 'string' ? parseJson(payload.content) : payload.content;
  return { toolUseId: payload.tool_use_id, value };
}

/**
 * #229: kortet slås opp blant alle samtalens kall, ikke bare de ventende — et
 * kall fra en assistentrad med tekst står alt på den boblen når resultatet kommer.
 */
function attachToolResultSummary(
  toolCalls: Map<string, ToolCallSummary>,
  result: ParsedToolResult,
): void {
  const target = toolCalls.get(result.toolUseId);
  if (!target) return;
  const summary = summariseToolResult(result.value);
  if (summary !== undefined) target.result_summary = summary;
}

/**
 * #212: brikken et `remember`/`forget`-kall ga i den live økten, gjenskapt fra
 * radene endepunktet allerede returnerer — `memory_changed` lagres ikke.
 * Samme regler som serveren (`memory-tools.ts`): et feilet kall gir ingen
 * brikke; `status: 'pending'` er «Foreslått for foreldrene»; `replaces_ref`
 * er «Oppdatert». Brikken får ingen `id`, og dermed ingen Angre (se
 * `MemoryChipEntry`). Teksten er den fra turen, ikke notatets tekst nå:
 * samtalen er bare eierens, og teksten står allerede i radene eieren får.
 */
function memoryChipFrom(result: ParsedToolResult, uses: MemoryToolUses): MemoryChipEntry | null {
  const use = uses.get(result.toolUseId);
  if (!use || !result.value || typeof result.value !== 'object') return null;
  const v = result.value as { ok?: unknown; ref?: unknown; status?: unknown; text?: unknown };
  if (v.ok !== true || typeof v.ref !== 'number') return null;

  if (use.name === 'forget') {
    return typeof v.text === 'string' ? { action: 'forgotten', ref: v.ref, text: v.text } : null;
  }
  const input = (use.input ?? {}) as { text?: unknown; replaces_ref?: unknown };
  if (typeof input.text !== 'string') return null;
  const action =
    v.status === 'pending' ? 'pending' : input.replaces_ref !== undefined ? 'updated' : 'saved';
  return { action, ref: v.ref, text: displayNoteText(input.text) };
}

/**
 * Modellens `remember.text` slik serveren lagret den, så langt det trengs for
 * visning (review-runde 1 i !182): NFKC, usynlige formattegn (`\p{Cf}`) bort
 * og mellomrom slått sammen, som `normalizeMemoryText` i api. ZWJ står igjen,
 * så emoji-sekvenser (👨‍👩‍👧) ikke brytes opp — serverens finere regel for ZWJ
 * mellom bokstaver betyr ingenting for hvordan teksten ser ut.
 */
function displayNoteText(raw: string): string {
  return raw.normalize('NFKC').replace(/(?!\u200D)\p{Cf}/gu, '').replace(/\s+/gu, ' ').trim();
}

function withAttachmentSuffix(text: string, hasImage: boolean): string {
  if (!hasImage) return text;
  return text.length > 0 ? `${text} 📎` : '📎';
}

export function hydrateThreadMessages(
  rows: AiConversationMessage[],
  viewerRole: ViewerRole,
): ThreadMessage[] {
  const out: ThreadMessage[] = [];
  const pendingToolCalls: ToolCallSummary[] = [];
  const toolCallsById = new Map<string, ToolCallSummary>();
  const memoryToolUses: MemoryToolUses = new Map();
  const pendingMemoryChanges: MemoryChipEntry[] = [];
  let skipNextUserAsCorrection = false;

  const noteToolUse = (use: ParsedToolUse): void => {
    const call: ToolCallSummary = { id: use.id, name: use.name, status: 'done' };
    pendingToolCalls.push(call);
    toolCallsById.set(use.id, call);
    if (use.name === 'remember' || use.name === 'forget') memoryToolUses.set(use.id, use);
  };

  /** Henger turens ventende verktøykort og hukommelsesbrikker på `message`. */
  const attachPending = (message: ThreadMessage): ThreadMessage => {
    if (pendingToolCalls.length > 0) message.toolCalls = pendingToolCalls.splice(0);
    if (pendingMemoryChanges.length > 0) message.memoryChanges = pendingMemoryChanges.splice(0);
    return message;
  };

  // #229: en tur som endte uten svartekst (feilet eller avbrutt) får kortene og
  // brikkene sine på en egen tom boble — før neste brukermelding og til slutt —
  // ikke på neste turs svar. Brikkene står som live (#193, #212).
  const flushTextlessTurn = (): void => {
    if (pendingToolCalls.length > 0 || pendingMemoryChanges.length > 0) {
      out.push(attachPending({ role: 'assistant', content: '' }));
    }
  };

  for (const row of rows) {
    if (row.role === 'tool_result') {
      const result = parseToolResult(row);
      if (!result) continue;
      attachToolResultSummary(toolCallsById, result);
      const chip = memoryChipFrom(result, memoryToolUses);
      if (chip) pendingMemoryChanges.push(chip);
      continue;
    }

    if (row.role === 'tool_use') {
      const parsedToolUse = parseToolUse(
        parseJson(row.content_json),
        row.tool_call_id,
        row.tool_name,
      );
      if (parsedToolUse) noteToolUse(parsedToolUse);
      continue;
    }

    const parsed = parseContent(row.content_json, row.tool_call_id, row.tool_name);
    parsed.toolUses.forEach(noteToolUse);

    if (row.role === 'user') {
      if (
        skipNextUserAsCorrection &&
        !parsed.hasImage &&
        isAllergyCorrectionPrompt(parsed.text)
      ) {
        skipNextUserAsCorrection = false;
        continue;
      }
      skipNextUserAsCorrection = false;
      const content = withAttachmentSuffix(parsed.text, parsed.hasImage);
      if (content.length === 0) continue;
      flushTextlessTurn();
      out.push({ role: 'user', content, ts: row.created_at });
      continue;
    }

    skipNextUserAsCorrection = row.filter_status === 'corrected';
    const content = withAttachmentSuffix(parsed.text, parsed.hasImage);
    const showBubble =
      content.length > 0 || row.filter_status === 'corrected' || (viewerRole === 'child' && row.food_adjacent);
    if (!showBubble) continue;

    out.push(
      attachPending({
        role: 'assistant',
        content,
        ts: row.created_at,
        filterCorrected: row.filter_status === 'corrected',
        advisoryLine: viewerRole === 'child' && row.food_adjacent,
      }),
    );
  }

  flushTextlessTurn();
  return out;
}
